import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    PlusCircle, Calendar, Link, MessageSquare, CheckCircle, Trash2, 
    Loader2, ListTodo, XCircle, Tag, Upload, Download, FileText, ChevronRight,
    BookOpenCheck 
} from 'lucide-react';

// =======================================================
// 1. 型定義と定数・ヘルパー
// =======================================================

/** タスクデータの型 */
interface Task {
    id: string; // ローカル識別子
    date: string; // YYYY-MM-DD
    title: string;
    category: string; // <-- 自由入力テキスト
    url?: string; 
    memo?: string; 
    isCompleted: boolean;
    createdAt: number; // タイムスタンプ (ソート用)
}

// インポート/エクスポート用のクリーンなタスク型
interface CleanTask {
    date: string;
    title: string;
    category: string;
    url?: string;
    memo?: string;
    isCompleted: boolean;
}

// タスクを日付でグループ化するための型
type GroupedTasks = { [date: string]: Task[] };

// カテゴリ表示用のデフォルトスタイル (自由入力のため固定色を使用)
const DEFAULT_CATEGORY_STYLE = 'bg-indigo-100 text-indigo-800 border-indigo-200';

// localStorageのキー
const LOCAL_STORAGE_KEY = 'localDailyTasksData';

/** 31日前の日付を 'YYYY-MM-DD' 形式で取得するヘルパー関数 (データ削除用) */
const getThirtyOneDaysAgoForDeletion = (): string => {
    const d = new Date();
    d.setDate(d.getDate() - 31);
    return d.toISOString().split('T')[0];
};

// =======================================================
// 2. メインコンポーネント
// =======================================================

const TaskPlannerView: React.FC = () => {
    // App Data State
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | null }>({ text: '', type: null });

    // UI Filter State
    const [filterUpcoming, setFilterUpcoming] = useState(true); 
    const [isFormOpen, setIsFormOpen] = useState(false); // ← 新規追加！

    // 削除確認モーダル用ステート
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);

    // Form State
    const today = new Date().toISOString().split('T')[0];
    const [newTaskDate, setNewTaskDate] = useState(today);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [newTaskCategory, setNewTaskCategory] = useState<string>('');
    const [newTaskUrl, setNewTaskUrl] = useState('');
    const [newTaskMemo, setNewTaskMemo] = useState('');

    // =======================================================
    // 3. データ永続化
    // =======================================================

    /** localStorageからタスクを読み込み、31日以上前のタスクを削除する (マウント時のみ実行) */
    useEffect(() => {
        try {
            const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
            let loadedTasks: Task[] = [];
            if (storedData) {
                const parsedTasks = JSON.parse(storedData) as Task[];
                // 1. 標準化 (ID/CreatedAtの補完)
                loadedTasks = parsedTasks.map(task => ({
                    ...task,
                    createdAt: task.createdAt || Date.now(),
                    id: task.id || crypto.randomUUID(), 
                }));
            }

            // --- 2. 31日以上前のタスクを削除するクリーンアップ処理 ---
            const cutoffDate = getThirtyOneDaysAgoForDeletion();
            const cleanedTasks = loadedTasks.filter(task => task.date >= cutoffDate);
            const deletedCount = loadedTasks.length - cleanedTasks.length;

            if (deletedCount > 0) {
                 setMessage({ text: `${deletedCount}件の古いタスクを自動削除しました。`, type: 'success' });
                 localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cleanedTasks));
            }
            
            setTasks(cleanedTasks);

        } catch (error) {
            console.error("Failed to load/clean tasks:", error);
            setMessage({ text: 'データの読み込みに失敗しました。', type: 'error' });
        } finally {
            setLoadingTasks(false);
        }
    }, []);

    useEffect(() => {
        if (!loadingTasks) {
            try {
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tasks));
            } catch (error) {
                console.error("Failed to save tasks:", error);
            }
        }
    }, [tasks, loadingTasks]);

    // =======================================================
    // 4. CRUD Operations
    // =======================================================

    const handleAddTask = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTaskTitle.trim() || !newTaskDate) {
            setMessage({ text: '日付とタイトルは必須です。', type: 'error' });
            return;
        }

        setSaving(true);
        setMessage({ text: '', type: null });

        try {
            const newTask: Task = {
                id: crypto.randomUUID(), 
                date: newTaskDate,
                title: newTaskTitle.trim(),
                category: newTaskCategory.trim() || '未分類', 
                url: newTaskUrl.trim() || '',
                memo: newTaskMemo.trim() || '',
                isCompleted: false,
                createdAt: Date.now(),
            };

            setTasks(prev => [...prev, newTask]);
            setMessage({ text: `「${newTaskTitle}」を登録しました！`, type: 'success' });

            // フォームリセット＆自動で閉じる
            setNewTaskDate(today);
            setNewTaskTitle('');
            setNewTaskCategory('');
            setNewTaskUrl('');
            setNewTaskMemo('');
            setIsFormOpen(false); // ← これで閉じる！

        } catch (error) {
            setMessage({ text: '登録に失敗しました。', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const toggleTaskCompletion = useCallback((taskId: string) => {
        setTasks(prev => 
            prev.map(task => 
                task.id === taskId ? { ...task, isCompleted: !task.isCompleted } : task
            )
        );
    }, []);

    const openDeleteConfirmation = useCallback((task: Task) => {
        setTaskToDelete(task);
        setIsDeleteModalOpen(true);
    }, []);

    const confirmDeleteTask = useCallback(() => {
        if (!taskToDelete) return;
        setTasks(prev => prev.filter(t => t.id !== taskToDelete.id));
        setMessage({ text: `「${taskToDelete.title}」を削除しました。`, type: 'success' });
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
    }, [taskToDelete]);

    const cancelDeleteTask = useCallback(() => {
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
    }, []);

    // =======================================================
    // 5. データ処理
    // =======================================================

    const groupedAndSortedTasks = useMemo(() => {
        const grouped: GroupedTasks = tasks.reduce((acc, task) => {
            acc[task.date] = acc[task.date] || [];
            acc[task.date].push(task);
            return acc;
        }, {} as GroupedTasks);

        Object.keys(grouped).forEach(date => {
            grouped[date].sort((a, b) => {
                if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
                return a.createdAt - b.createdAt;
            });
        });

        let sortedDates = Object.keys(grouped);
        if (filterUpcoming) {
            sortedDates = sortedDates.filter(date => date >= today);
        }
        sortedDates.sort((a, b) => a.localeCompare(b));
        
        return { grouped, sortedDates };
    }, [tasks, filterUpcoming, today]); 

    // =======================================================
    // 6. Import/Export
    // =======================================================

    const handleExportTasks = () => {
        if (tasks.length === 0) {
            setMessage({ text: 'エクスポートするタスクがありません。', type: 'error' });
            return;
        }

        const cleanTasks: CleanTask[] = tasks.map(t => ({
            date: t.date,
            title: t.title,
            category: t.category,
            url: t.url,
            memo: t.memo,
            isCompleted: t.isCompleted,
        }));

        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(cleanTasks, null, 2));
        const a = document.createElement('a');
        a.setAttribute("href", dataStr);
        a.setAttribute("download", `task_export_${today}.json`);
        document.body.appendChild(a);
        a.click();
        a.remove();

        setMessage({ text: 'エクスポート完了！', type: 'success' });
    };

    const handleImportTasks = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonText = e.target?.result as string;
                const imported: CleanTask[] = JSON.parse(jsonText);
                
                if (!Array.isArray(imported)) throw new Error("無効なデータです");

                const newTasks: Task[] = imported.map(t => ({
                    id: crypto.randomUUID(),
                    date: t.date,
                    title: t.title,
                    category: t.category || '未分類',
                    url: t.url || '',
                    memo: t.memo || '',
                    isCompleted: !!t.isCompleted,
                    createdAt: Date.now(),
                }));
                
                setTasks(prev => [...prev, ...newTasks]);
                setMessage({ text: `${newTasks.length}件インポートしました！`, type: 'success' });

            } catch (error) {
                setMessage({ text: `インポート失敗: ${error instanceof Error ? error.message : '形式エラー'}`, type: 'error' });
            } finally {
                if (event.target) event.target.value = '';
            }
        };
        reader.readAsText(file);
    };

    // =======================================================
    // 7. UI Components
    // =======================================================

    const ConfirmationModal: React.FC<{
        isOpen: boolean;
        task: Task | null;
        onConfirm: () => void;
        onCancel: () => void;
    }> = ({ isOpen, task, onConfirm, onCancel }) => {
        if (!isOpen || !task) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
                    <div className="p-6">
                        <div className="flex items-center mb-4">
                            <XCircle className="w-8 h-8 text-red-500 mr-3" />
                            <h3 className="text-xl font-bold text-gray-800">削除確認</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            「{task.title}」を完全に削除しますか？
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button onClick={onCancel} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-semibold">
                                キャンセル
                            </button>
                            <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold">
                                <Trash2 className="w-4 h-4 inline mr-1" /> 削除
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const TaskItem: React.FC<{ task: Task }> = React.memo(({ task }) => {
        const urlText = task.url ? task.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') : '';

        return (
            <div className={`flex items-start p-3 border-b last:border-b-0 transition-all ${task.isCompleted ? 'bg-green-50/50 opacity-70' : 'bg-white'}`}>
<input
    type="checkbox"
    checked={task.isCompleted}
    onChange={() => toggleTaskCompletion(task.id)}
    className="h-5 w-5 rounded border-2 border-gray-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 cursor-pointer transition-all mt-1"
/>

                <div className="flex-1 ml-4 overflow-hidden">
                    <div className="flex items-center justify-between mb-1">
                        <p className={`font-semibold text-base ${task.isCompleted ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                            {task.title}
                        </p>
                        <span className={`text-xspx-2 py-0.5 rounded-full border ${DEFAULT_CATEGORY_STYLE}`}>
                            {task.category}
                        </span>
                    </div>

                    {task.url && (
                        <a href={task.url} target="_blank" rel="noopener noreferrer"
                           className={`text-base font-medium text-indigo-600 hover:underline flex items-center mt-1 ${task.isCompleted ? 'line-through opacity-70' : ''}`}>
                            <Link className="w-4 h-4 mr-1" />
                            {urlText}
                        </a>
                    )}

                    {task.memo && (
                        <div className={`mt-1.5 text-xs p-2 rounded-lg ${task.isCompleted ? 'bg-gray-200 text-gray-600' : 'bg-yellow-100 text-yellow-800'}`}>
                            <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />
                            {task.memo}
                        </div>
                    )}
                </div>

                <button onClick={() => openDeleteConfirmation(task)}
                        className="ml-4 p-1 text-red-400 hover:text-red-600 mt-0.5">
                    <Trash2 className="w-5 h-5" />
                </button>
            </div>
        );
    });

    // =======================================================
    // 8. メインレンダリング
    // =======================================================
    
    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto min-h-screen bg-gray-50">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-100 mb-8">
                {/* ヘッダー */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                    <h2 className="text-3xl font-extrabold text-indigo-700 flex items-center">
                        <BookOpenCheck className="w-7 h-7 mr-3 text-red-500" />
                        タスク管理
                        <span className="ml-3 px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full">BETA</span>
                    </h2>

                    <div className="flex space-x-2 mt-4 sm:mt-0">
                        <button onClick={handleExportTasks}
                                className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center text-sm font-medium"
                                disabled={tasks.length === 0}>
                            <Download className="w-4 h-4 mr-1" /> Export
                        </button>
                        <label htmlFor="import-file"
                               className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center cursor-pointer text-sm font-medium">
                            <Upload className="w-4 h-4 mr-1" /> Import
                            <input id="import-file" type="file" accept=".json" onChange={handleImportTasks} className="hidden" />
                        </label>
                    </div>
                </div>

                <p className="text-sm text-red-400 font-medium border-b border-gray-200 pb-4 mb-6 text-left">
                    <ListTodo className="w-4 h-4 inline mr-1.5" />
                    ベータ版：データは端末ローカル保存
                </p>

                {/* メッセージ */}
                {message.text && (
                    <div className={`mb-6 p-4 rounded-xl font-semibold text-center border-2 ${
                        message.type === 'success' 
                            ? 'bg-green-50 border-green-300 text-green-800' 
                            : 'bg-red-50 border-red-300 text-red-800'
                    } flex items-center justify-center`}>
                        {message.type === 'success' ? <CheckCircle className="w-5 h-5 mr-3" /> : <XCircle className="w-5 h-5 mr-3" />}
                        {message.text}
                    </div>
                )}

                {/* 開閉式登録フォーム */}
                <div className="mt-8">
                    <button
                        onClick={() => setIsFormOpen(!isFormOpen)}
                        className={`w-full py-5 px-6 rounded-2xl font-bold text-lg transition-all duration-300 flex items-center justify-center shadow-xl ${
                            isFormOpen 
                                ? 'bg-red-500 hover:bg-red-600 text-white' 
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        }`}
                    >
                        {isFormOpen ? (
                            <>閉じる ×</>
                        ) : (
                            <>
                                <PlusCircle className="w-7 h-7 mr-3" />
                                新しいタスクを登録
                            </>
                        )}
                    </button>

                    {/* フォーム本体（スライドアニメーション） */}
                    <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        isFormOpen ? 'max-h-screen opacity-100 mt-6' : 'max-h-0 opacity-0'
                    }`}>
                        <div className="bg-gradient-to-b from-indigo-50 to-white p-6 rounded-2xl border-2 border-indigo-200 shadow-inner">
                            <form onSubmit={handleAddTask} className="space-y-5">
                                <h3 className="text-xl font-bold text-indigo-700 flex items-center">
                                    タスク詳細入力
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 flex items-center"><Calendar className="w-4 h-4 mr-1 text-red-500" /> 日付</label>
                                        <input type="date" value={newTaskDate} onChange={e => setNewTaskDate(e.target.value)}
                                               className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-200" required disabled={saving} />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 flex items-center"><Tag className="w-4 h-4 mr-1 text-red-500" /> カテゴリ</label>
                                        <input type="text" placeholder="例: 資格, 読書" value={newTaskCategory}
                                               onChange={e => setNewTaskCategory(e.target.value)}
                                               className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-200" disabled={saving} />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 flex items-center"><FileText className="w-4 h-4 mr-1 text-red-500" /> タイトル</label>
                                    <input type="text" placeholder="例: 数学10問復習" value={newTaskTitle}
                                           onChange={e => setNewTaskTitle(e.target.value)}
                                           className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-200" required disabled={saving} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 flex items-center"><Link className="w-4 h-4 mr-1 text-indigo-500" /> URL</label>
                                    <input type="url" placeholder="https://..." value={newTaskUrl}
                                           onChange={e => setNewTaskUrl(e.target.value)}
                                           className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-200" disabled={saving} />
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 flex items-center"><MessageSquare className="w-4 h-4 mr-1 text-indigo-500" /> メモ</label>
                                    <textarea rows={2} placeholder="詳細メモ..." value={newTaskMemo}
                                              onChange={e => setNewTaskMemo(e.target.value)}
                                              className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-200" disabled={saving} />
                                </div>

                                <button
                                    type="submit"
                                    disabled={saving || !newTaskTitle || !newTaskDate}
                                    className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50 flex items-center justify-center"
                                >
                                    {saving ? (
                                        <>登録中... <Loader2 className="w-6 h-6 ml-2 animate-spin" /></>
                                    ) : (
                                        'タスクを登録する！'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

           {/* タスクリスト表示セクション */}
           <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-100">
                
                {/* 登録済みタスク一覧 タイトルとフィルタ */}
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Calendar className="w-6 h-6 mr-2 text-indigo-600" />
                        登録済みタスク一覧
                    </h3>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">
                            今後のタスクのみ表示
                        </span>
 
                        <input
                            type="checkbox"
                            checked={filterUpcoming}
                            onChange={() => setFilterUpcoming(prev => !prev)}
                            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                    </div>
                </div>

                {loadingTasks && (
                    <div className="mb-6 p-4 rounded-xl font-semibold text-center bg-indigo-50 text-indigo-700">
                        <Loader2 className="w-5 h-5 inline mr-2 animate-spin" />
                        ローカルデータを読み込み中...
                    </div>
                )}
                
                {!loadingTasks && tasks.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border-dashed border-2">
                        <p className="text-lg font-medium">まだタスクが登録されていません。</p>
                        <p className="text-sm mt-1">上のフォームから最初のタスクを登録してみましょう！</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {groupedAndSortedTasks.sortedDates.map(date => (
                            <div key={date} className="border-2 border-indigo-100 rounded-xl overflow-hidden shadow-md">
                                {/* 日付ヘッダー */}
                                <div className="p-3 bg-indigo-50 font-bold text-indigo-700 text-lg flex justify-between items-center">
                                    <span className="flex items-center">
                                        {date === today ? (
                                            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full mr-2">今日</span>
                                        ) : null}
                                        {date}
                                    </span>
                                    <span className="text-sm font-medium text-gray-500">
                                        {groupedAndSortedTasks.grouped[date].filter(t => t.isCompleted).length} / {groupedAndSortedTasks.grouped[date].length} 完了
                                    </span>
                                </div>
                                
                                {/* タスクリスト */}
                                <div className="divide-y divide-gray-100">
                                    {groupedAndSortedTasks.grouped[date].map(task => (
                                        <TaskItem key={task.id} task={task} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 削除確認モーダルをレンダリング */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                task={taskToDelete}
                onConfirm={confirmDeleteTask}
                onCancel={cancelDeleteTask}
            />
        </div>
    );
};

// Wrapper component to make it runnable
const App: React.FC = () => {
    return <TaskPlannerView />;
}

export default App;